import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/EmptyState";
import { AuthenticatedImage } from "@/components/shared/AuthenticatedImage";
import { UserStatusBadge } from "@/components/shared/Badges";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ShieldCheck, XCircle, Store, Mail, Phone, MapPin } from "lucide-react";

const DOC_LABEL = {
    shop_business_license: "Business License",
    pan_or_aadhaar: "PAN / Aadhaar",
    shop_photo_or_offer_proof: "Shop Photo / Offer Proof",
    business_address_proof: "Address Proof",
};

const REQUIRED_DOC_FIELDS = ["shop_business_license", "pan_or_aadhaar", "shop_photo_or_offer_proof", "business_address_proof"];

const shopMissingDocs = (shop) => {
    const docs = shop?.verification_docs || {};
    return REQUIRED_DOC_FIELDS.filter((f) => !docs[f]);
};

export default function AdminShopsPendingPage() {
    const [shops, setShops] = useState([]);
    const [uploads, setUploads] = useState({}); // upload_id -> record
    const [loading, setLoading] = useState(true);
    // Phase 13d — bulk approve: set of selected shop ids (approvable only).
    const [selected, setSelected] = useState(() => new Set());
    const [bulkBusy, setBulkBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const res = await api.get("/admin/users?role=SHOP_OWNER&status=PENDING_APPROVAL");
            setShops(res.data);
            setSelected(new Set()); // reset selections on refresh
            setUploads({});
        } catch (e) {
            toast.error(errorMessage(e));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const approvableIds = useMemo(
        () => shops.filter((s) => shopMissingDocs(s).length === 0).map((s) => s.id),
        [shops],
    );
    const allApprovableSelected = approvableIds.length > 0 && approvableIds.every((id) => selected.has(id));

    const toggleOne = (id, checked) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id); else next.delete(id);
            return next;
        });
    };

    const toggleSelectAll = () => {
        if (allApprovableSelected) setSelected(new Set());
        else setSelected(new Set(approvableIds));
    };

    const clearSelection = () => setSelected(new Set());

    const runBulkApprove = async () => {
        const ids = Array.from(selected).filter((id) => approvableIds.includes(id));
        if (!ids.length) return;
        setBulkBusy(true);
        const progressToast = toast.loading(`Approving 0 of ${ids.length}…`);
        let done = 0;
        try {
            const results = await Promise.allSettled(
                ids.map(async (id) => {
                    try {
                        await api.post(`/admin/users/${id}/approve-shop`);
                    } finally {
                        done += 1;
                        toast.loading(`Approving ${done} of ${ids.length}…`, { id: progressToast });
                    }
                }),
            );
            const ok = results.filter((r) => r.status === "fulfilled").length;
            const failed = results.length - ok;
            if (failed === 0) {
                toast.success(`Approved ${ok} shops`, { id: progressToast, duration: 4000 });
            } else {
                toast.error(`Approved ${ok}, ${failed} failed`, { id: progressToast, duration: 5000 });
            }
        } finally {
            setBulkBusy(false);
            await load();
        }
    };

    const selectedCount = Array.from(selected).filter((id) => approvableIds.includes(id)).length;

    return (
        <div className="space-y-6" data-testid="admin-shops-pending-page">
            <header>
                <div className="font-heading text-sm font-semibold text-[var(--brand-orange)]">Shop verification</div>
                <h1 className="mt-1 font-heading text-3xl font-extrabold text-[var(--text-primary)] sm:text-4xl">
                    Pending shops
                </h1>
                <p className="mt-1 font-body text-sm text-[var(--text-muted)]">
                    Review documents and approve or reject shop-owner accounts.
                </p>
            </header>

            {shops.length > 0 && !loading && (
                <div className="card-soft flex flex-wrap items-center justify-between gap-3" data-testid="admin-shops-bulk-toolbar">
                    <label className="flex items-center gap-3 font-body text-sm text-[var(--text-primary)]">
                        <Checkbox
                            checked={allApprovableSelected}
                            onCheckedChange={toggleSelectAll}
                            disabled={approvableIds.length === 0 || bulkBusy}
                            data-testid="admin-shops-select-all"
                        />
                        <span>
                            Select all approvable
                            <span className="ml-1 text-xs text-[var(--text-muted)]">
                                ({approvableIds.length} of {shops.length} eligible)
                            </span>
                        </span>
                    </label>
                    {selectedCount > 0 && (
                        <div className="flex items-center gap-3">
                            <button
                                type="button"
                                onClick={clearSelection}
                                disabled={bulkBusy}
                                className="font-body text-xs font-medium text-[var(--text-muted)] underline-offset-2 hover:underline"
                                data-testid="admin-shops-clear-selection"
                            >
                                Clear selection
                            </button>
                            <ConfirmBulkApprove
                                count={selectedCount}
                                busy={bulkBusy}
                                onConfirm={runBulkApprove}
                            />
                        </div>
                    )}
                </div>
            )}

            {loading ? (
                <div className="space-y-4">
                    <Skeleton className="h-40 rounded-2xl" />
                    <Skeleton className="h-40 rounded-2xl" />
                </div>
            ) : shops.length ? (
                <div className="space-y-4">
                    {shops.map((s) => (
                        <ShopCard
                            key={s.id}
                            shop={s}
                            onChanged={load}
                            selected={selected.has(s.id)}
                            onToggleSelect={(checked) => toggleOne(s.id, checked)}
                        />
                    ))}
                </div>
            ) : (
                <EmptyState
                    icon={ShieldCheck}
                    title="Inbox zero"
                    hint="There are no shops waiting for verification right now."
                />
            )}
        </div>
    );
}

const ConfirmBulkApprove = ({ count, busy, onConfirm }) => (
    <AlertDialog>
        <AlertDialogTrigger asChild>
            <Button
                className="btn-primary"
                disabled={busy}
                data-testid="admin-shops-bulk-approve-btn"
            >
                <ShieldCheck size={14} className="mr-2" /> Approve {count} selected
            </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
            <AlertDialogHeader>
                <AlertDialogTitle>Approve {count} shops?</AlertDialogTitle>
                <AlertDialogDescription>
                    This will approve each selected shop and notify the owner. Only rows with all required
                    documents are eligible; missing-doc rows were skipped automatically.
                </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
                <AlertDialogCancel data-testid="admin-shops-bulk-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onConfirm} data-testid="admin-shops-bulk-confirm">
                    Approve {count}
                </AlertDialogAction>
            </AlertDialogFooter>
        </AlertDialogContent>
    </AlertDialog>
);

const ShopCard = ({ shop, onChanged, selected, onToggleSelect }) => {
    const docs = shop.verification_docs || {};
    const missingDocs = REQUIRED_DOC_FIELDS.filter((f) => !docs[f]);
    const canApprove = missingDocs.length === 0;

    const approve = async () => {
        try {
            await api.post(`/admin/users/${shop.id}/approve-shop`);
            toast.success(
                `Shop approved. Gift issued: 3 Flash Credits, Launch Promo available.`,
                { duration: 5000 },
            );
            onChanged?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };

    return (
        <div className="card-soft" data-testid={`shop-card-${shop.email}`}>
            <div className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
                <div className="flex items-start gap-3">
                    <Checkbox
                        checked={!!selected}
                        onCheckedChange={(v) => onToggleSelect?.(!!v)}
                        disabled={!canApprove}
                        aria-label={canApprove ? `Select ${shop.business_name || shop.email}` : "Cannot select — missing documents"}
                        className="mt-1"
                        data-testid={`admin-shops-select-${shop.id}`}
                    />
                    <div className="flex items-center gap-3">
                        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-[var(--brand-orange)] text-white shadow-sm">
                            <Store size={18} />
                        </div>
                        <div>
                            <div className="font-heading text-lg font-bold text-[var(--text-primary)]" data-testid="shop-name">
                                {shop.business_name || "(no business name)"}
                            </div>
                            <div className="mt-0.5 flex flex-wrap items-center gap-3 font-body text-xs text-[var(--text-muted)]">
                                <span className="inline-flex items-center gap-1"><Mail size={12} /> {shop.email}</span>
                                {shop.mobile_number && (
                                    <span className="inline-flex items-center gap-1"><Phone size={12} /> {shop.mobile_number}</span>
                                )}
                                {shop.business_district && (
                                    <span className="inline-flex items-center gap-1 capitalize"><MapPin size={12} /> {shop.business_district}</span>
                                )}
                                {shop.business_category && (
                                    <span className="capitalize">• {shop.business_category?.replace(/-/g, " ")}</span>
                                )}
                                {shop.business_type && <span>• {shop.business_type}</span>}
                            </div>
                        </div>
                    </div>
                </div>
                <StatusPill status={shop.status} />
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {Object.entries(DOC_LABEL).map(([key, label]) => (
                    <DocPreview key={key} label={label} uploadId={docs?.[key]} />
                ))}
            </div>

            <div className="mt-5 flex flex-wrap items-center justify-end gap-3">
                {!canApprove && (
                    <span
                        className="font-body text-xs text-rose-600"
                        data-testid={`missing-docs-hint-${shop.email}`}
                    >
                        Missing documents: {missingDocs.map((f) => DOC_LABEL[f]).join(", ")}
                    </span>
                )}
                <RejectDialog shop={shop} onDone={onChanged} />
                <Button
                    onClick={approve}
                    disabled={!canApprove}
                    className="btn-primary disabled:cursor-not-allowed disabled:opacity-50"
                    data-testid={`approve-btn-${shop.email}`}
                    title={canApprove ? "Approve this shop" : "Cannot approve — required documents are missing"}
                >
                    <ShieldCheck size={14} className="mr-2" /> Approve
                </Button>
            </div>
        </div>
    );
};

/** Phase 13c — color-coded status pill used across admin moderation surfaces. */
const StatusPill = ({ status }) => {
    const map = {
        ACTIVE: { cls: "status-pill status-pill-approved", label: "Approved" },
        PENDING_APPROVAL: { cls: "status-pill status-pill-pending", label: "Pending" },
        REJECTED: { cls: "status-pill status-pill-rejected", label: "Rejected" },
        SUSPENDED: { cls: "status-pill status-pill-rejected", label: "Suspended" },
    };
    const s = map[status] || { cls: "status-pill status-pill-neutral", label: status || "—" };
    return <span className={s.cls} data-testid={`status-pill-${status}`}>{s.label}</span>;
};

const DocPreview = ({ label, uploadId }) => {
    // We only have upload id; we don't know extension without a lookup. Try common ones.
    // Approach: skip preview if id missing, otherwise attempt to construct a URL. In Phase 1a we
    // returned `filename` = `<id>.<ext>`. Uploads made before this UI existed (seed) use fake ids
    // like `seed-doc-xxx`. So we can only preview docs uploaded via THIS UI where we tracked
    // filename separately. For simplicity, render a small badge with an "Open" fallback link.
    if (!uploadId) {
        return (
            <div className="flex flex-col rounded-xl border border-dashed border-neutral-300 p-3 text-center text-xs text-neutral-500">
                <div className="font-semibold text-neutral-700">{label}</div>
                <div className="mt-2">Not uploaded</div>
            </div>
        );
    }
    return (
        <div className="flex flex-col rounded-xl border border-neutral-200 bg-neutral-50 p-3 text-xs">
            <div className="font-semibold text-neutral-700">{label}</div>
            <div className="mt-2 text-[11px] text-neutral-500 break-all">
                Upload id: {uploadId.slice(0, 12)}…
            </div>
            <div className="mt-2 flex-1">
                {/* AuthenticatedImage handles auth headers; we guess a PNG URL from the id.
                    Real filename resolution can be added when we expose a GET-by-id endpoint. */}
                <AuthenticatedImage
                    src={`/api/uploads/${uploadId}.png`}
                    className="aspect-[4/3] w-full"
                    testId={`doc-preview-${uploadId}`}
                />
            </div>
        </div>
    );
};

const RejectDialog = ({ shop, onDone }) => {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("");
    const submit = async () => {
        try {
            await api.post(`/admin/users/${shop.id}/reject-shop`, {
                rejection_reason: reason,
            });
            toast.success(`Rejected ${shop.business_name || shop.email}`);
            setOpen(false);
            setReason("");
            onDone?.();
        } catch (e) {
            toast.error(errorMessage(e));
        }
    };
    return (
        <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
                <Button variant="outline" className="text-rose-600" data-testid={`reject-btn-${shop.email}`}>
                    <XCircle size={14} className="mr-2" /> Reject
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
                <DialogHeader>
                    <DialogTitle>Reject shop application?</DialogTitle>
                    <DialogDescription>
                        Share the reason with the applicant so they know what
                        to fix.
                    </DialogDescription>
                </DialogHeader>
                <Input
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. Business license unreadable"
                    data-testid={`reject-reason-${shop.email}`}
                />
                <DialogFooter>
                    <Button
                        disabled={!reason.trim()}
                        onClick={submit}
                        className="bg-rose-600 text-white hover:bg-rose-700"
                        data-testid={`reject-confirm-${shop.email}`}
                    >
                        Reject application
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
